/**
 * "Is this record about them?" — a question whose answer is an identity
 * verdict: the record whole — its words, its source, what the identity
 * check compared where it says — and under it the four answers, a key each:
 * About them (1), A connection (2), Not them (3, then why: b · c · r · n ·
 * o) and Not sure (4); Enter records. Once each of the subject's other open
 * identity questions has been opened, "All not them" answers them together,
 * with one reason.
 */

import { useState } from 'react';

import { Button, Chip, Group, Loader, Stack, Text, TextInput } from '@mantine/core';

import { useWorkbenchEvidenceItem } from '@/hooks/useWorkbench';
import {
  identifiersOf,
  NOT_THEM_REASONS,
  type Intervention,
  type NotThemReason,
  type WorkbenchHeader,
} from '@/types/workbench';
import { askRecord } from '../needsYouOrder';
import { comparedOn, IdentifiersTable, RecordView } from '../RecordView';
import { useWorkbenchKeys } from '../useWorkbenchKeys';
import { AskHeading, Kbd, listOf, textOf, useAnswer } from './common';
import classes from '../NeedsYou.module.css';

type Decision = 'same' | 'connected' | 'different' | 'uncertain';

const ANSWERS: { value: Decision; label: string; key: string }[] = [
  { value: 'same', label: 'About them', key: '1' },
  { value: 'connected', label: 'A connection', key: '2' },
  { value: 'different', label: 'Not them', key: '3' },
  { value: 'uncertain', label: 'Not sure', key: '4' },
];

interface IdentityPaneProps {
  header: WorkbenchHeader;
  ask: Intervention;
  subjectName: string | null;
  /** The subject's other open identity questions. */
  others: Intervention[];
  /** The questions opened this session. */
  opened: ReadonlySet<string>;
  onAnswered: (ids: string[]) => void | Promise<void>;
  /** Take the keys (the pane in view). */
  keyboard: boolean;
}

/** What an older question said was compared, where it carries no table. */
function comparedText(ask: Intervention): string | null {
  const matched = listOf(ask.payload.matched);
  const conflicting = listOf(ask.payload.conflicting);
  const parts = [
    matched.length ? `Matches: ${matched.join(', ')}` : '',
    conflicting.length ? `Conflicts: ${conflicting.join(', ')}` : '',
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

export function IdentityPane({
  header,
  ask,
  subjectName,
  others,
  opened,
  onAnswered,
  keyboard,
}: Readonly<IdentityPaneProps>) {
  const record = useWorkbenchEvidenceItem(header.reportId, askRecord(ask), header.version);
  const { submitting, submit, submitMany } = useAnswer(header.reportId, onAnswered);
  const [decision, setDecision] = useState<Decision | null>(null);
  const [reason, setReason] = useState<NotThemReason | null>(null);
  const [note, setNote] = useState('');
  const [restReason, setRestReason] = useState<NotThemReason | null>(null);
  const ready = decision !== null && (decision !== 'different' || reason !== null);

  const choose = (next: Decision) => {
    setDecision(next);
    if (next !== 'different') {
      setReason(null);
    }
  };

  const record_ = () => {
    if (!ready || decision === null || submitting) {
      return;
    }
    void submit(ask, {
      identity: { decision, ...(decision === 'different' && reason ? { reason } : {}) },
      ...(note.trim() ? { note: note.trim() } : {}),
    });
  };

  const keys: Record<string, () => void> = { Enter: record_ };
  for (const a of ANSWERS) {
    keys[a.key] = () => choose(a.value);
  }
  if (decision === 'different') {
    for (const r of NOT_THEM_REASONS) {
      keys[r.key] = () => setReason(r.value);
    }
  }
  useWorkbenchKeys(keys, keyboard);

  // The table: the record's own comparison, else the question's.
  const fromRecord = record.data ? comparedOn(record.data) : [];
  const fromAsk = identifiersOf(ask.payload);
  const older = fromRecord.length === 0 && fromAsk.length === 0 ? comparedText(ask) : null;
  const allOpened = others.every((o) => opened.has(o.id));

  return (
    <>
      <div className={classes.detailBody} data-testid="pane-identity">
        <Stack gap="sm">
          <AskHeading
            section={ask.section}
            subjectName={subjectName}
            question={ask.question}
            why={ask.reason}
            blocking={ask.waitingTaskId !== null}
          />
          {fromRecord.length === 0 && fromAsk.length > 0 && (
            <IdentifiersTable identifiers={fromAsk} subjectName={subjectName} />
          )}
          {older && (
            <Text size="xs" c="dimmed">
              {older}
            </Text>
          )}
          {record.data ? (
            <RecordView record={record.data} reportId={header.reportId} subjectName={subjectName} />
          ) : record.error ? (
            <Text size="sm" c="dimmed">
              {textOf(ask.payload.statement) ?? "The record couldn't be read."}
            </Text>
          ) : (
            <Loader size="sm" />
          )}
        </Stack>
      </div>
      <Stack gap={6} className={classes.answers} data-testid={`identity-bar-${ask.id}`}>
        <Group gap={6} wrap="wrap">
          {ANSWERS.map((a) => (
            <Button
              key={a.value}
              size="xs"
              variant={decision === a.value ? 'filled' : 'default'}
              color={a.value === 'different' ? 'red' : a.value === 'same' ? 'teal' : 'blue'}
              onClick={() => choose(a.value)}
              aria-pressed={decision === a.value}
              data-testid={`identity-${a.value}`}
            >
              {a.label}
              <Kbd>{a.key}</Kbd>
            </Button>
          ))}
        </Group>
        {decision === 'different' && (
          <Group gap={6} wrap="wrap" data-testid="not-them-reasons">
            <Text size="xs" c="dimmed">
              Why not them?
            </Text>
            {NOT_THEM_REASONS.map((r) => (
              <Chip
                key={r.value}
                size="xs"
                variant="outline"
                checked={reason === r.value}
                onChange={() => setReason(r.value)}
                data-testid={`reason-${r.value}`}
              >
                {r.label} ({r.key})
              </Chip>
            ))}
          </Group>
        )}
        <Group gap={6} wrap="nowrap">
          <TextInput
            size="xs"
            placeholder="A note with the answer (optional)"
            aria-label="Note"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Button
            size="xs"
            onClick={record_}
            disabled={!ready}
            loading={submitting}
            data-testid="identity-record"
          >
            Record{decision ? ` “${ANSWERS.find((a) => a.value === decision)?.label}”` : ''}
            <Kbd>Enter</Kbd>
          </Button>
        </Group>
        {others.length > 0 && (
          <Stack gap={4} data-testid="identity-rest">
            <Text size="xs" c="dimmed">
              The other {others.length} about {subjectName ?? 'them'}
              {allOpened ? ' — not them, because:' : " (once you've opened each)"}
            </Text>
            <Group gap={6} wrap="wrap">
              {NOT_THEM_REASONS.map((r) => (
                <Chip
                  key={r.value}
                  size="xs"
                  variant="outline"
                  checked={restReason === r.value}
                  onChange={() => setRestReason(r.value)}
                  disabled={!allOpened}
                  data-testid={`rest-reason-${r.value}`}
                >
                  {r.label}
                </Chip>
              ))}
              <Button
                size="compact-xs"
                variant="default"
                disabled={!allOpened || restReason === null}
                loading={submitting}
                onClick={() =>
                  restReason &&
                  void submitMany(
                    others.map((o) => ({
                      ask: o,
                      answer: { identity: { decision: 'different' as const, reason: restReason } },
                    }))
                  )
                }
                data-testid="identity-all-not-them"
              >
                All not them
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </>
  );
}
